import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  User,
  UserRoundCog,
  SearchCode,
  Building2,
  BarChart3,
  MessageSquarePlus,
  MessageSquare,
  UserPlus,
  Shield,
  MessagesSquare,
  MessageSquareDashed,
  Logs,
  Wand2,
  GitBranch,
  Webhook,
} from 'lucide-react';
import { APP_VERSION } from '../config.js';
import { useChatContext } from '../context/useChatContext.js';
import { attendantHasPermission } from '../utils/role.js';
import { parseTimestampToDate } from '../utils/datetime.js';
import { syncBrowserAttentionCount } from '../utils/browserAttention.js';
import { subscribeConversationEvents } from '../websockets/conversationsHub.js';
import {
  INTERNAL_CHAT_UNREAD_UPDATED_EVENT,
  getInternalChatUnreadMapKey,
  getInternalChatUnreadThreadIds,
  getInternalChatUnreadTotal,
  readInternalChatUnreadMap,
} from '../utils/internalChatUnread.js';
import WhatsAppsModalLauncher from './WhatsAppsModalLauncher.jsx';


import './Sidebar.css';

function Sidebar({
  collapsed,
  setCollapsed,
  isMobile = false,
  isMobileOpen = false,
  onRequestClose = undefined,
}) {
  const isHiddenFromChatIndicatorsStatus = useCallback((value) => {
    const status = String(value ?? '').toLowerCase();
    return (
      status.includes('waiting_evaluation') ||
      status.includes('workflow_waiting_input') ||
      status.includes('workflow_handling')
    );
  }, []);

  const resolveSlotUpdatedAt = (slot) => {
    if (!slot) return 0;
    const candidates = [
      slot.updated_at,
      slot.last_message_at,
      slot.closed_at,
      slot.created_at,
    ];
    for (const value of candidates) {
      const date = parseTimestampToDate(value);
      if (date) return date.getTime();
    }
    return 0;
  };

  const readWaitingAckMap = () => {
    try {
      const raw = localStorage.getItem('chat_waiting_ack_v1');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const getGroupLastSeenKey = useCallback((companyIdValue) => {
    const cid = companyIdValue ?? 'none';
    return `group_last_seen_v1__${cid}`;
  }, []);

  const readGroupLastSeen = useCallback((companyIdValue) => {
    try {
      const raw = localStorage.getItem(getGroupLastSeenKey(companyIdValue));
      return raw ? Number(raw) : 0;
    } catch {
      return 0;
    }
  }, [getGroupLastSeenKey]);


  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    previousPathRef.current = currentPath;

    if (!isMobile || !isMobileOpen || typeof onRequestClose !== 'function') return;
    if (previousPath === currentPath) return;
    onRequestClose();
  }, [isMobile, isMobileOpen, location.pathname, onRequestClose]);

  // ===== restante do seu código para dados de usuário/papel =====
  const {
    waitingList: contextWaitingList,
    attendingList: contextAttendingList,
    allList: contextAllList,
    normalizedAttendantId,
    companySettings,
    isAdmin,
    isSuperAdmin,
    isGlobalAdmin,
    companyId,
    hasCompany,
    attendantData,
    canAccessInternalChat,
    getConversationUnreadCount,
  } = useChatContext();

  // Usuários globais sem empresa selecionada veem apenas rotas globais permitidas.
  const showOnlyCompanyNav = !hasCompany && isGlobalAdmin === true;
  const isReportsRoute = location.pathname.startsWith('/reports');
  const groupsEnabled = Boolean(companySettings?.groups_enabled);
  const transmissionEnabled = Boolean(companySettings?.transmission_enabled);
  const hasPerm = (slug) => attendantHasPermission(attendantData, slug);
  const canManageAttendants = hasPerm('attendants:manage') || isAdmin;
  const canManageTeams = hasPerm('teams:manage') || isAdmin;
  const canReadContacts = hasPerm('contacts:read');
  const canReadConversations = hasPerm('conversations:read');
  const canManageWhatsapp = hasPerm('whatsapp:manage') || isAdmin;
  const canReadGroups = hasPerm('groups:read');
  const showInternalChat = hasCompany && Boolean(canAccessInternalChat);
  const canViewAnalytics = hasPerm('analytics:view') || isAdmin;
  const canConfigureClientUpdates = isGlobalAdmin;
  const canManageWebhooks = isSuperAdmin === true;
  const showGroups = hasCompany && groupsEnabled && canReadGroups;
  const canAccessTaskBlue =
    hasCompany &&
    transmissionEnabled &&
    (isGlobalAdmin || Boolean(attendantData?.can_access_taskblue));

  // ===== Chat indicators (unread + waiting) =====
  const [waitingAckMap, setWaitingAckMap] = useState(() => readWaitingAckMap());
  const [hasUnread, setHasUnread] = useState(false);
  const [hasWaiting, setHasWaiting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [waitingCount, setWaitingCount] = useState(0);
  const [groupLastSeen, setGroupLastSeen] = useState(() => readGroupLastSeen(companyId));
  const [latestGroupMessageTs, setLatestGroupMessageTs] = useState(0);
  const [hasGroupUnread, setHasGroupUnread] = useState(false);
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const groupUnreadDedupeRef = useRef(new Set());
  const groupLastSeenRef = useRef(groupLastSeen);
  const [internalUnreadCount, setInternalUnreadCount] = useState(() =>
    getInternalChatUnreadTotal(readInternalChatUnreadMap(companyId, normalizedAttendantId))
  );
  const [internalUnreadThreads, setInternalUnreadThreads] = useState(() =>
    getInternalChatUnreadThreadIds(
      readInternalChatUnreadMap(companyId, normalizedAttendantId)
    )
  );
  const [hasInternalUnread, setHasInternalUnread] = useState(false);
  const internalUnreadThreadsCount = internalUnreadThreads.length;

  const formatCount = (n) => {
    const v = Number(n) || 0;
    if (v > 99) return '99+';
    return String(v);
  };

  const syncInternalChatUnreadState = useCallback((companyIdValue, attendantIdValue) => {
    if (!companyIdValue || !attendantIdValue) {
      setInternalUnreadCount(0);
      setInternalUnreadThreads([]);
      return;
    }
    const unreadMap = readInternalChatUnreadMap(companyIdValue, attendantIdValue);
    setInternalUnreadCount(getInternalChatUnreadTotal(unreadMap));
    setInternalUnreadThreads(getInternalChatUnreadThreadIds(unreadMap));
  }, []);

  const normalizedWaitingList = useMemo(
    () => (Array.isArray(contextWaitingList) ? contextWaitingList : []),
    [contextWaitingList]
  );
  const normalizedAttendingList = useMemo(
    () => (Array.isArray(contextAttendingList) ? contextAttendingList : []),
    [contextAttendingList]
  );
  const relevantSlots = useMemo(() => {
    const deduped = [];
    const seen = new Set();


    const addSlot = (slot) => {
      if (!slot || slot.id == null) return;
      const key = String(slot.id);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(slot);
    };

    normalizedAttendingList.forEach(addSlot);
    normalizedWaitingList.forEach(addSlot);

    const currentAttendantId =
      normalizedAttendantId === null || normalizedAttendantId === undefined
        ? null
        : Number(normalizedAttendantId);

    if (currentAttendantId !== null && !Number.isNaN(currentAttendantId)) {
      const all = Array.isArray(contextAllList) ? contextAllList : [];
      all.forEach((slot) => {
        const rawAttendant =
          slot?.attendantId ?? slot?.attendant_id ?? slot?.attendant?.id ?? null;
        if (rawAttendant === null || rawAttendant === undefined) return;
        const parsed = Number(rawAttendant);
        if (Number.isNaN(parsed) || parsed !== currentAttendantId) {
          return;
        }
        addSlot(slot);
      });
    }

    return deduped;
  }, [contextAllList, normalizedAttendingList, normalizedWaitingList, normalizedAttendantId]);

  const renderNavLabel = (label) => (
    <span className="nav-label">
      {label}
    </span>
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncFromStorage = () => {
      const nextWaitingAck = readWaitingAckMap();
      setWaitingAckMap((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(nextWaitingAck)) {
          return prev;
        }
        return nextWaitingAck;
      });

      setGroupLastSeen(showGroups ? readGroupLastSeen(companyId) : 0);
      syncInternalChatUnreadState(companyId, normalizedAttendantId);
    };

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', syncFromStorage);
    window.addEventListener('bluezap:waiting-ack-updated', syncFromStorage);

    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', syncFromStorage);
      window.removeEventListener('bluezap:waiting-ack-updated', syncFromStorage);
    };
  }, [
    companyId,
    normalizedAttendantId,
    readGroupLastSeen,
    showGroups,
    syncInternalChatUnreadState,
  ]);

  useEffect(() => {
    const waiting = normalizedWaitingList;
    const all = relevantSlots;

    const newWaiting = waiting.filter((slot) => {
      if (!slot || slot.id == null) return false;
      if (isHiddenFromChatIndicatorsStatus(slot?.handling_status)) return false;
      const id = String(slot.id);
      const ackTs = Number(waitingAckMap?.[id] || 0);
      if (!ackTs) return true;
      const updatedAt = resolveSlotUpdatedAt(slot);
      if (!updatedAt) return false;
      return updatedAt > ackTs;
    });

    const totalUnread = all.reduce((acc, slot) => {
      if (isHiddenFromChatIndicatorsStatus(slot?.handling_status)) return acc;
      return acc + getConversationUnreadCount(slot);
    }, 0);

    setUnreadCount(totalUnread);
    setHasUnread(totalUnread > 0);
    setWaitingCount(newWaiting.length);
    setHasWaiting(newWaiting.length > 0);
  }, [getConversationUnreadCount, isHiddenFromChatIndicatorsStatus, normalizedWaitingList, relevantSlots, waitingAckMap]);

  useEffect(() => {
    groupLastSeenRef.current = groupLastSeen;
  }, [groupLastSeen]);

  useEffect(() => {
    setGroupLastSeen(readGroupLastSeen(companyId));
    setLatestGroupMessageTs(0);
    setHasGroupUnread(false);
    setGroupUnreadCount(0);
    groupUnreadDedupeRef.current = new Set();
    setHasInternalUnread(false);
    syncInternalChatUnreadState(companyId, normalizedAttendantId);
  }, [companyId, normalizedAttendantId, readGroupLastSeen, syncInternalChatUnreadState]);

  useEffect(() => {
    if (showGroups) return;
    setGroupLastSeen(0);
    setLatestGroupMessageTs(0);
    setHasGroupUnread(false);
    setGroupUnreadCount(0);
    groupUnreadDedupeRef.current = new Set();
  }, [showGroups]);

  useEffect(() => {
    if (!showInternalChat) {
      setInternalUnreadCount(0);
      setInternalUnreadThreads([]);
      return undefined;
    }
    if (!companyId || !normalizedAttendantId || typeof window === 'undefined') return undefined;

    const handleUnreadUpdated = (event) => {
      const targetCompanyId = Number(event?.detail?.companyId ?? companyId);
      if (Number.isFinite(targetCompanyId) && targetCompanyId !== Number(companyId)) {
        return;
      }
      const targetAttendantId = Number(
        event?.detail?.attendantId ?? normalizedAttendantId
      );
      if (
        Number.isFinite(targetAttendantId) &&
        targetAttendantId !== Number(normalizedAttendantId)
      ) {
        return;
      }
      syncInternalChatUnreadState(companyId, normalizedAttendantId);
    };

    const handleStorage = (event) => {
      if (
        event?.key &&
        event.key !== getInternalChatUnreadMapKey(companyId, normalizedAttendantId)
      ) {
        return;
      }
      syncInternalChatUnreadState(companyId, normalizedAttendantId);
    };

    window.addEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [companyId, normalizedAttendantId, showInternalChat, syncInternalChatUnreadState]);

  useEffect(() => {
    if (!hasCompany || !showGroups) return;
    const currentPath = location.pathname;
    if (currentPath === '/groups') {
      const referenceTs = latestGroupMessageTs || Date.now();
      setGroupLastSeen((prev) => {
        const next = referenceTs > prev ? referenceTs : prev;
        if (next !== prev) {
          try {
            localStorage.setItem(getGroupLastSeenKey(companyId), String(next));
          } catch {
            // Persistencia local e opcional; ignoramos falhas de storage.
          }
        }
        return next;
      });
      setHasGroupUnread(false);
      setGroupUnreadCount(0);
      groupUnreadDedupeRef.current = new Set();
    }
  }, [
    getGroupLastSeenKey,
    location.pathname,
    hasCompany,
    companyId,
    latestGroupMessageTs,
    showGroups,
  ]);

  useEffect(() => {
    if (!showGroups || !hasCompany || companyId === null || companyId === undefined) return undefined;

    const unsubscribe = subscribeConversationEvents(companyId, {
      onMessage: (payload) => {
        try {
          const type = (payload?.type || '').toLowerCase();
          if (type !== 'group_message') return;
          const message = payload?.message || {};
          const group = payload?.group || {};
          const isFromMeSignal =
            message?.message_key_from_me ??
            message?.is_from_me ??
            message?.isFromMe ??
            false;
          const isOwnMessage = isFromMeSignal === true;

          const ts =
            parseTimestampToDate(message?.timestamp || message?.created_at)?.getTime() ||
            Date.now();
          if (isOwnMessage) return;
          setLatestGroupMessageTs((prev) => (ts > prev ? ts : prev));

          const groupId = Number(group?.id);
          const messageKey =
            message?.baileys_message_id ||
            message?.baileysMessageId ||
            message?.message_remote_id ||
            message?.messageRemoteId ||
            message?.message_key_id ||
            message?.messageKeyId ||
            message?.id ||
            `${ts}`;
          const dedupeKey = `${Number.isFinite(groupId) ? groupId : 'group'}:${String(messageKey)}`;
          if (groupUnreadDedupeRef.current.has(dedupeKey)) return;
          groupUnreadDedupeRef.current.add(dedupeKey);

          if (Number.isFinite(ts) && ts > Number(groupLastSeenRef.current || 0)) {
            setGroupUnreadCount((prev) => prev + 1);
          }
        } catch {
          // ignore
        }
      },
    }, {
      source: 'Sidebar',
    });

    return () => {
      unsubscribe();
    };
  }, [hasCompany, companyId, showGroups]);

  useEffect(() => {
    setHasGroupUnread(groupUnreadCount > 0);
  }, [groupUnreadCount]);

  useEffect(() => {
    const unread = internalUnreadCount > 0 || internalUnreadThreadsCount > 0;
    setHasInternalUnread(unread);
  }, [internalUnreadCount, internalUnreadThreadsCount]);

  const browserAttentionCount = useMemo(() => {
    const chatUnread = Number(unreadCount) || 0;
    const internalUnread = Number(internalUnreadCount) || 0;
    const groupUnread = Number(groupUnreadCount) || 0;
    return chatUnread + internalUnread + groupUnread;
  }, [groupUnreadCount, internalUnreadCount, unreadCount]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const syncAttention = () => {
      if (document.visibilityState === 'visible') {
        syncBrowserAttentionCount(0);
        return;
      }
      syncBrowserAttentionCount(browserAttentionCount);
    };

    syncAttention();
    document.addEventListener('visibilitychange', syncAttention);

    return () => {
      document.removeEventListener('visibilitychange', syncAttention);
    };
  }, [browserAttentionCount]);

  const sidebarClass = [
    'sidebar',
    !isMobile && collapsed ? 'collapsed' : '',
    isMobile ? 'sidebar--mobile' : '',
    isMobile ? (isMobileOpen ? 'sidebar--mobile-open' : 'sidebar--mobile-hidden') : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleMobileNavInteraction = (event) => {
    if (!isMobile || typeof onRequestClose !== 'function') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const actionableElement = target.closest('a, button');
    if (actionableElement) onRequestClose();
  };

  return (
    <>
      <div className={sidebarClass}>
        {/* <div className={`sidebar-brand ${collapsed ? 'sidebar-brand--collapsed' : ''}`}>
          <span className="sidebar-brand-text">BlueZap</span>
        </div>
         */}
        {/* Topo com Menu + ícone */}
        <div className="sidebar-shell-header">
          <div className="sidebar-menu-title">
            {/* em compacto/colapsado o texto some via CSS */}
            {!collapsed && <span className="sidebar-menu-text">Menu</span>}

          </div>

          {!isMobile && (
            <button
              type="button"
              className="sidebar-collapse-button"
              onClick={() => setCollapsed(!collapsed)}
            >
              <Logs size={19} />
            </button>
          )}
        </div>

        {/* Menu de navegação */}
        <nav className="nav-menu" onClickCapture={handleMobileNavInteraction}>
          {showOnlyCompanyNav ? (
            <>
              {isSuperAdmin && (
                <Link
                  to="/companyRegister"
                  className={location.pathname === '/companyRegister' ? 'active' : ''}
                  data-tooltip="Empresas"
                >
                  <Building2 size={18} />
                  {renderNavLabel('Empresas')}
                </Link>
              )}
              <Link
                to="/client-updates"
                className={location.pathname === '/client-updates' ? 'active' : ''}
                data-tooltip="Gerenciar novidades"
              >
                <Wand2 size={18} />
                {renderNavLabel('Novidades (Admin)')}
              </Link>
              {isSuperAdmin && (
                <Link
                  to="/workflow-actions"
                  className={location.pathname === '/workflow-actions' ? 'active' : ''}
                  data-tooltip="Ações do workflow"
                >
                  <Shield size={18} />
                  {renderNavLabel('Ações do workflow')}
                </Link>
              )}
              {canManageWebhooks && (
                <Link
                  to="/webhooks"
                  className={location.pathname === '/webhooks' ? 'active' : ''}
                  data-tooltip="Webhooks"
                >
                  <Webhook size={18} />
                  {renderNavLabel('Webhooks')}
                </Link>
              )}
            </>
          ) : (
            <>
              {isSuperAdmin && (
                <Link
                  to="/companyRegister"
                  className={location.pathname === '/companyRegister' ? 'active' : ''}
                  data-tooltip="Empresas"
                >
                  <Building2 size={18} />
                  {renderNavLabel('Empresas')}
                </Link>
              )}

              <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''} data-tooltip="Dashboard">
                <Home size={18} />
                {renderNavLabel('Dashboard')}
              </Link>

              {hasCompany && canManageAttendants && (
                <Link to="/attendants" className={location.pathname === '/attendants' ? 'active' : ''} data-tooltip="Atendentes">
                  <Users size={18} />
                  {renderNavLabel('Atendentes')}
                </Link>
              )}

              {hasCompany && canReadContacts && (

                <Link
                  to="/contacts"
                  className={['/contacts', '/clients'].includes(location.pathname) ? 'active' : ''}
                  data-tooltip="Contatos"
                >

                  <User size={18} />
                  {renderNavLabel('Contatos')}
                </Link>
              )}

              {hasCompany && canManageTeams && (
                <Link to="/queues" className={['/queues', '/teams'].includes(location.pathname) ? 'active' : ''} data-tooltip="Filas">
                  <UserRoundCog size={18} />
                  {renderNavLabel('Filas')}
                </Link>
              )}

              {hasCompany && canReadConversations && (
                <Link
                  to="/chatpage"
                  className={location.pathname === '/chatpage' ? 'active' : ''}
                  data-tooltip="Chat"
                >
                  <span className="nav-icon-wrap" aria-hidden="true">
                    <MessageSquare size={18} />
                  </span>
                  {renderNavLabel('Chat')}
                  <span className="nav-right-indicators" aria-hidden="true">
                    {hasUnread && (
                      <span className="nav-right-item nav-right-item--unread" title="Novas mensagens">
                        <MessageSquarePlus size={16} />
                        <span className="nav-right-count">{formatCount(unreadCount)}</span>
                      </span>
                    )}
                    {hasWaiting && (
                      <span className="nav-right-item nav-right-item--waiting" title="Contatos na fila">
                        <UserPlus size={16} />
                        <span className="nav-right-count">{formatCount(waitingCount)}</span>
                      </span>
                    )}
                  </span>
                </Link>
              )}

              {showGroups && (
                <Link
                  to="/groups"
                  className={location.pathname === '/groups' ? 'active' : ''}
                  data-tooltip="Grupos"
                >
                  <span className="nav-icon-wrap" aria-hidden="true">
                    <MessagesSquare size={18} />
                  </span>
                  {renderNavLabel('Grupos')}
                  <span className="nav-right-indicators" aria-hidden="true">
                    {hasGroupUnread && (
                      <span className="nav-right-item nav-right-item--unread" title="Novas mensagens em grupos">
                        <MessageSquarePlus size={16} />
                        {groupUnreadCount > 0 && (
                          <span className="nav-right-count">{formatCount(groupUnreadCount)}</span>
                        )}
                      </span>
                    )}
                  </span>
                </Link>
              )}

              {showInternalChat && (
                <Link
                  to="/internal-chat"
                  className={location.pathname === '/internal-chat' ? 'active' : ''}
                  data-tooltip="Chat interno"
                >
                  <MessageSquareDashed size={18} />
                  {renderNavLabel('Chat interno')}
                  <span className="nav-right-indicators" aria-hidden="true">
                    {hasInternalUnread && (
                      <span className="nav-right-item nav-right-item--unread" title="Novas mensagens internas">
                        <MessageSquarePlus size={13} />
                        {internalUnreadCount > 0 && (
                          <span className="nav-right-count">
                            {formatCount(internalUnreadCount)}
                          </span>
                        )}
                      </span>
                    )}
                    {internalUnreadThreadsCount > 0 && (
                      <span className="nav-right-item nav-right-item--threads" title="Chats com mensagens recebidas">
                        <UserPlus size={13} />
                        <span className="nav-right-count">
                          {formatCount(internalUnreadThreadsCount)}
                        </span>
                      </span>
                    )}
                  </span>
                </Link>
              )}

              {hasCompany && canReadConversations && (
                <Link to="/conversations" className={location.pathname === '/conversations' ? 'active' : ''} data-tooltip="Atendimentos">
                  <SearchCode size={18} />
                  {renderNavLabel('Atendimentos')}
                </Link>
              )}

              {hasCompany && canViewAnalytics && (
                <>
                  <Link to="/reports" className={isReportsRoute ? 'active' : ''} data-tooltip="Relatórios">
                    <BarChart3 size={18} />
                    {renderNavLabel('Relatórios')}
                  </Link>
                </>
              )}

              {canConfigureClientUpdates && (
                <Link
                  to="/client-updates"
                  className={location.pathname === '/client-updates' ? 'active' : ''}
                  data-tooltip="Gerenciar novidades"
                >
                  <Wand2 size={18} />
                  {renderNavLabel('Novidades (Admin)')}
                </Link>
              )}

              {isSuperAdmin && (
                <Link
                  to="/workflow-actions"
                  className={location.pathname === '/workflow-actions' ? 'active' : ''}
                  data-tooltip="Ações do workflow"
                >
                  <Shield size={18} />
                  {renderNavLabel('Ações do workflow')}
                </Link>
              )}
              {canManageWebhooks && (
                <Link
                  to="/webhooks"
                  className={location.pathname === '/webhooks' ? 'active' : ''}
                  data-tooltip="Webhooks"
                >
                  <Webhook size={18} />
                  {renderNavLabel('Webhooks')}
                </Link>
              )}

              {hasCompany && isGlobalAdmin && (
                <Link to="/workflows" className={location.pathname === '/workflows' ? 'active' : ''} data-tooltip="Workflow do bot">
                  <GitBranch size={18} />
                  {renderNavLabel('Workflow do bot')}
                </Link>
              )}

              {canAccessTaskBlue && (
                <Link
                  to="/transmissions"
                  className={location.pathname === '/transmissions' ? 'active' : ''}
                  data-tooltip="Task Blue"
                >
                  <MessageSquarePlus size={18} />
                  {renderNavLabel('Task Blue')}
                </Link>
              )}

              {hasCompany && canManageWhatsapp && (
                <WhatsAppsModalLauncher collapsed={collapsed} />
              )}

            </>
          )}
        </nav>

        <footer className="sidebar-version" aria-label={`Versão do sistema BlueZap ${APP_VERSION}`}>
          BlueZap <span className="sidebar-version__value">{APP_VERSION}</span>
        </footer>
      </div>
    </>
  );
}

export default Sidebar;
